import React, { useState } from 'react';
import { FileText, Download, CheckCircle2, Clock, Car, AlertTriangle } from 'lucide-react';
import { updateInspection, logAuditEvent } from '../lib/supabase';

// jsPDF is loaded via window.jspdf from CDN — see index.html
// We import it dynamically to avoid SSR issues
const getJsPDF = () => {
  if (window.jspdf) return window.jspdf.jsPDF;
  throw new Error('jsPDF not loaded');
};

// ── Helpers ───────────────────────────────────────────────────────────────────

const val = (v, fallback = '—') => (v !== undefined && v !== null && v !== '') ? v : fallback;


const conditionLabel = (v) => {
  if (v === 'pass') return 'Pass';
  if (v === 'fail') return 'FAIL';
  if (v === 'na') return 'N/A';
  return '—';
};

const PANELS = [
  'Hood', 'Roof', 'Trunk / Tailgate', 'Front Bumper', 'Rear Bumper',
  'Driver Front Door', 'Driver Rear Door', 'Passenger Front Door', 'Passenger Rear Door',
  'Driver Front Fender', 'Driver Rear Quarter', 'Passenger Front Fender', 'Passenger Rear Quarter',
  'Windshield', 'Rear Glass',
  'Driver Front Wheel', 'Driver Rear Wheel', 'Passenger Front Wheel', 'Passenger Rear Wheel',
];

const FUNCTION_ITEMS = [
  { field: 'starts', label: 'Vehicle starts' },
  { field: 'idles', label: 'Idles smoothly' },
  { field: 'transmission', label: 'Transmission' },
  { field: 'headlights', label: 'Headlights' },
  { field: 'taillights', label: 'Taillights / brake lights' },
  { field: 'turnsignals', label: 'Turn signals' },
  { field: 'hazards', label: 'Hazard lights' },
  { field: 'reverselights', label: 'Reverse lights' },
  { field: 'ac', label: 'Air conditioning' },
  { field: 'heat', label: 'Heat / defrost' },
  { field: 'wipers', label: 'Wipers & washers' },
  { field: 'horn', label: 'Horn' },
  { field: 'windows', label: 'Power windows' },
  { field: 'mirrors', label: 'Power mirrors' },
];

const INTERIOR_ITEMS = [
  { field: 'driverSeat', label: 'Driver seat' },
  { field: 'passengerSeat', label: 'Passenger seat' },
  { field: 'rearSeats', label: 'Rear seats' },
  { field: 'headliner', label: 'Headliner' },
  { field: 'carpet', label: 'Carpet / mats' },
  { field: 'dashboard', label: 'Dashboard' },
  { field: 'infotainment', label: 'Infotainment' },
  { field: 'sunroof', label: 'Sunroof / moonroof' },
];

const ENGINE_ITEMS = [
  { field: 'engineOil', label: 'Engine oil' },
  { field: 'coolant', label: 'Coolant' },
  { field: 'brakeFluid', label: 'Brake fluid' },
  { field: 'powerSteering', label: 'Power steering fluid' },
  { field: 'washerFluid', label: 'Washer fluid' },
  { field: 'engineLeaks', label: 'No visible leaks' },
  { field: 'beltsHoses', label: 'Belts & hoses' },
  { field: 'battery', label: 'Battery' },
  { field: 'airFilter', label: 'Air filter' },
];

// ── PDF Builder ───────────────────────────────────────────────────────────────

async function buildPDF(inspectionData) {
  const jsPDF = getJsPDF();
  const doc = new jsPDF({ unit: 'pt', format: 'letter' });

  const W = 612; // letter width in pts
  const MARGIN = 48;
  const COL = W - MARGIN * 2;
  let y = 0;

  const vi = inspectionData.vehicleInfo || {};
  const fn = inspectionData.function_data || {};
  const doc_data = inspectionData.documentation_data || {};
  const ext = inspectionData.exterior_data || {};
  const int_data = inspectionData.interior_data || {};
  const eng = inspectionData.engine_data || {};
  const keys = inspectionData.keys_data || {};
  const bol = inspectionData.bol_data || {};

  // ── Colours & fonts ──
  const BLUE = [37, 99, 235];
  const DARK = [15, 23, 42];
  const GRAY = [100, 116, 139];
  const LIGHT_BG = [248, 250, 252];
  const BORDER = [226, 232, 240];
  const GREEN = [22, 101, 52];
  const RED = [220, 38, 38];
  const AMBER = [180, 83, 9];

  const setFont = (style = 'normal', size = 10) => {
    doc.setFont('helvetica', style);
    doc.setFontSize(size);
  };

  const setColor = (rgb) => doc.setTextColor(...rgb);

  // Add a new page and reset y
  const newPage = () => {
    doc.addPage();
    y = MARGIN;
  };

  // Check if we need a new page
  const checkPage = (needed = 40) => {
    if (y + needed > 760) newPage();
  };

  // Horizontal rule
  const hr = (color = BORDER, thickness = 0.5) => {
    doc.setDrawColor(...color);
    doc.setLineWidth(thickness);
    doc.line(MARGIN, y, W - MARGIN, y);
    y += 8;
  };

  // Section header
  const sectionHeader = (title, icon = null) => {
    checkPage(50);
    y += 8;
    doc.setFillColor(...BLUE);
    doc.rect(MARGIN, y, COL, 24, 'F');
    setFont('bold', 11);
    setColor([255, 255, 255]);
    doc.text(title.toUpperCase(), MARGIN + 10, y + 15);
    y += 32;
  };

  // Key-value row
  const kvRow = (label, value, labelWidth = 150) => {
    checkPage(20);
    setFont('bold', 9);
    setColor(GRAY);
    doc.text(label, MARGIN, y);
    setFont('normal', 10);
    setColor(DARK);
    doc.text(String(val(value)), MARGIN + labelWidth, y);
    y += 16;
  };

  // Two-column key-value
  const kvRow2 = (l1, v1, l2, v2) => {
    checkPage(18);
    const mid = MARGIN + COL / 2;
    setFont('bold', 9);
    setColor(GRAY);
    doc.text(l1, MARGIN, y);
    setFont('normal', 10);
    setColor(DARK);
    doc.text(String(val(v1)), MARGIN + 110, y);
    setFont('bold', 9);
    setColor(GRAY);
    doc.text(l2, mid, y);
    setFont('normal', 10);
    setColor(DARK);
    doc.text(String(val(v2)), mid + 110, y);
    y += 16;
  };

  // Pass/Fail table row

  // Two-column condition table
  const condTable2col = (items, data) => {
    const mid = MARGIN + COL / 2 + 4;
    for (let i = 0; i < items.length; i += 2) {
      checkPage(18);
      const a = items[i];
      const b = items[i + 1];
      // Left
      setFont('normal', 10);
      setColor(DARK);
      doc.text(a.label, MARGIN + 4, y);
      const aLabel = conditionLabel(data[a.field]);
      const aColor = data[a.field] === 'pass' ? GREEN : data[a.field] === 'fail' ? RED : GRAY;
      setFont('bold', 9);
      setColor(aColor);
      doc.text(aLabel, mid - 10, y, { align: 'right' });
      // Right
      if (b) {
        setFont('normal', 10);
        setColor(DARK);
        doc.text(b.label, mid + 4, y);
        const bLabel = conditionLabel(data[b.field]);
        const bColor = data[b.field] === 'pass' ? GREEN : data[b.field] === 'fail' ? RED : GRAY;
        setFont('bold', 9);
        setColor(bColor);
        doc.text(bLabel, W - MARGIN - 4, y, { align: 'right' });
      }
      y += 15;
      doc.setDrawColor(...BORDER);
      doc.setLineWidth(0.3);
      doc.line(MARGIN, y - 2, W - MARGIN, y - 2);
    }
  };

  // Notes block
  const notesBlock = (label, text) => {
    if (!text) return;
    checkPage(40);
    setFont('bold', 9);
    setColor(GRAY);
    doc.text(label.toUpperCase(), MARGIN, y);
    y += 14;
    setFont('normal', 10);
    setColor(DARK);
    const lines = doc.splitTextToSize(String(text), COL);
    lines.forEach(line => {
      checkPage(14);
      doc.text(line, MARGIN, y);
      y += 14;
    });
    y += 4;
  };

  // Embed a photo from URL
  const embedPhoto = async (url, x, photoY, w, h) => {
    try {
      const resp = await fetch(url);
      const blob = await resp.blob();
      const dataUrl = await new Promise((resolve) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.readAsDataURL(blob);
      });
      doc.addImage(dataUrl, 'JPEG', x, photoY, w, h);
    } catch (e) {
      // If photo fails to load draw a placeholder box
      doc.setFillColor(...LIGHT_BG);
      doc.setDrawColor(...BORDER);
      doc.rect(x, photoY, w, h, 'FD');
      setFont('normal', 8);
      setColor(GRAY);
      doc.text('Photo unavailable', x + w / 2, photoY + h / 2, { align: 'center' });
    }
  };

  // Photo grid — up to 3 per row
  const photoGrid = async (photos = [], caption = '') => {
    if (!photos || photos.length === 0) return;
    const PW = (COL - 16) / 3;
    const PH = PW * 0.75;
    checkPage(PH + 30);
    if (caption) {
      setFont('bold', 9);
      setColor(GRAY);
      doc.text(caption.toUpperCase(), MARGIN, y);
      y += 14;
    }
    let col = 0;
    let rowY = y;
    for (const url of photos) {
      if (col === 3) { col = 0; rowY += PH + 8; checkPage(PH + 16); }
      const px = MARGIN + col * (PW + 8);
      await embedPhoto(url, px, rowY, PW, PH);
      col++;
    }
    y = rowY + PH + 16;
  };

  // ══════════════════════════════════════════════════════════════
  // PAGE 1 — HEADER + VEHICLE INFO
  // ══════════════════════════════════════════════════════════════
  y = MARGIN;

  // Header banner
  doc.setFillColor(...BLUE);
  doc.rect(0, 0, W, 72, 'F');
  setFont('bold', 20);
  setColor([255, 255, 255]);
  doc.text('VEHICLE CONDITION REPORT', MARGIN, 34);
  setFont('normal', 10);
  setColor([147, 197, 253]);
  const dateStr = new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  doc.text(`Generated: ${dateStr}`, MARGIN, 52);
  if (inspectionData.inspectionId) {
    doc.text(`Inspection ID: ${inspectionData.inspectionId}`, W - MARGIN, 52, { align: 'right' });
  }

  y = 90;

  // Vehicle identity block
  doc.setFillColor(...LIGHT_BG);
  doc.setDrawColor(...BORDER);
  doc.rect(MARGIN, y, COL, 80, 'FD');
  y += 16;

  setFont('bold', 16);
  setColor(DARK);
  const vehicleTitle = [vi.year, vi.make, vi.model].filter(Boolean).join(' ') || 'Vehicle';
  doc.text(vehicleTitle, MARGIN + 12, y);
  y += 18;

  kvRow2('VIN', vi.vin, 'Asset ID', vi.assetId);
  kvRow2('Odometer', vi.odometer ? `${Number(vi.odometer).toLocaleString()} miles` : '—', 'Location', vi.location);

  y += 8;
  hr();

  // ── BOL & Keys summary ──
  sectionHeader('Documentation & Keys');

  kvRow2('BOL Provided', bol.bolProvided ? 'Yes' : 'No', 'License Plate', doc_data.licensePlate);
  kvRow2('Mechanical Keys', val(keys.mechanicalKeys, 0), 'Key FOBs', val(keys.keyFobs, 0));
  kvRow2('Registration Expires', val(doc_data.registrationExpiry), 'Insurance Expires', val(doc_data.insuranceExpiry));

  const docChecks = [
    { field: 'licensePlatePresent', label: 'License plate' },
    { field: 'registrationPresent', label: 'Registration' },
    { field: 'insurancePresent', label: 'Insurance card' },
    { field: 'titlePresent', label: 'Title' },
  ];
  y += 4;
  setFont('bold', 9);
  setColor(GRAY);
  doc.text('DOCUMENTS PRESENT', MARGIN, y);
  y += 14;
  docChecks.forEach(({ field, label }) => {
    checkPage(16);
    const present = doc_data[field];
    const color = present ? GREEN : AMBER;
    setFont('normal', 10);
    setColor(DARK);
    doc.text(label, MARGIN + 4, y);
    setFont('bold', 9);
    setColor(color);
    doc.text(present ? 'Yes' : 'Not confirmed', W - MARGIN - 4, y, { align: 'right' });
    y += 14;
    doc.setDrawColor(...BORDER);
    doc.setLineWidth(0.3);
    doc.line(MARGIN, y - 2, W - MARGIN, y - 2);
  });

  notesBlock('Documentation notes', doc_data.documentationNotes);

  // ── Vehicle Function ──
  sectionHeader('Vehicle Function');

  const failedItems = FUNCTION_ITEMS.filter(item => fn[item.field] === 'fail');
  if (failedItems.length > 0) {
    checkPage(30);
    doc.setFillColor(254, 242, 242);
    doc.setDrawColor(254, 202, 202);
    doc.rect(MARGIN, y, COL, 20, 'FD');
    setFont('bold', 10);
    setColor(RED);
    doc.text(`⚠ ${failedItems.length} item(s) failed: ${failedItems.map(i => i.label).join(', ')}`, MARGIN + 8, y + 13);
    y += 28;
  }

  condTable2col(FUNCTION_ITEMS, fn);
  notesBlock('Function notes', fn.functionNotes);

  // ══════════════════════════════════════════════════════════════
  // PAGE 2+ — EXTERIOR
  // ══════════════════════════════════════════════════════════════
  newPage();
  sectionHeader('Exterior Inspection');

  // Panel condition table
  const damagedPanels = PANELS.filter(p => {
    const key = 'panel_' + p.toLowerCase().replace(/[^a-z]/g, '_');
    return ext[key] === 'damage';
  });

  if (damagedPanels.length > 0) {
    checkPage(30);
    doc.setFillColor(254, 242, 242);
    doc.setDrawColor(254, 202, 202);
    doc.rect(MARGIN, y, COL, 20, 'FD');
    setFont('bold', 10);
    setColor(RED);
    doc.text(`⚠ Damage noted on ${damagedPanels.length} panel(s): ${damagedPanels.join(', ')}`, MARGIN + 8, y + 13);
    y += 28;
  } else {
    checkPage(24);
    doc.setFillColor(240, 253, 244);
    doc.setDrawColor(187, 247, 208);
    doc.rect(MARGIN, y, COL, 20, 'FD');
    setFont('bold', 10);
    setColor(GREEN);
    doc.text('✓ No panel damage noted', MARGIN + 8, y + 13);
    y += 28;
  }

  setFont('bold', 9);
  setColor(GRAY);
  doc.text('PANEL CONDITION', MARGIN, y);
  y += 14;

  for (let i = 0; i < PANELS.length; i += 2) {
    checkPage(18);
    const mid = MARGIN + COL / 2 + 4;
    const pa = PANELS[i];
    const pb = PANELS[i + 1];
    const ka = 'panel_' + pa.toLowerCase().replace(/[^a-z]/g, '_');
    const statusA = ext[ka];

    setFont('normal', 10);
    setColor(DARK);
    doc.text(pa, MARGIN + 4, y);
    const aColor = statusA === 'ok' ? GREEN : statusA === 'damage' ? RED : GRAY;
    setFont('bold', 9);
    setColor(aColor);
    doc.text(statusA === 'ok' ? 'OK' : statusA === 'damage' ? 'DAMAGE' : '—', mid - 10, y, { align: 'right' });

    if (pb) {
      const kb = 'panel_' + pb.toLowerCase().replace(/[^a-z]/g, '_');
      const statusB = ext[kb];
      setFont('normal', 10);
      setColor(DARK);
      doc.text(pb, mid + 4, y);
      const bColor = statusB === 'ok' ? GREEN : statusB === 'damage' ? RED : GRAY;
      setFont('bold', 9);
      setColor(bColor);
      doc.text(statusB === 'ok' ? 'OK' : statusB === 'damage' ? 'DAMAGE' : '—', W - MARGIN - 4, y, { align: 'right' });
    }

    y += 15;
    doc.setDrawColor(...BORDER);
    doc.setLineWidth(0.3);
    doc.line(MARGIN, y - 2, W - MARGIN, y - 2);
  }

  notesBlock('Exterior damage notes', ext.exteriorNotes);

  // Exterior photos
  const extPhotos = [
    ...(ext.ext_front || []),
    ...(ext.ext_rear || []),
    ...(ext.ext_driver || []),
    ...(ext.ext_passenger || []),
    ...(ext.ext_damage_photos || []),
  ];
  await photoGrid(extPhotos, 'Exterior Photos');

  // ══════════════════════════════════════════════════════════════
  // Interior
  // ══════════════════════════════════════════════════════════════
  checkPage(60);
  sectionHeader('Interior Inspection');

  condTable2col(INTERIOR_ITEMS, int_data);

  if (int_data.odor && int_data.odor !== 'none') {
    checkPage(24);
    y += 4;
    setFont('bold', 9);
    setColor(GRAY);
    doc.text('ODOR', MARGIN, y);
    y += 14;
    setFont('normal', 10);
    setColor(AMBER);
    doc.text(int_data.odor.charAt(0).toUpperCase() + int_data.odor.slice(1), MARGIN + 4, y);
    y += 16;
  }

  notesBlock('Interior notes', int_data.interiorNotes);

  const intPhotos = [
    ...(int_data.int_dashboard || []),
    ...(int_data.int_front_seats || []),
    ...(int_data.int_rear_seats || []),
    ...(int_data.int_cargo || []),
    ...(int_data.int_damage_photos || []),
  ];
  await photoGrid(intPhotos, 'Interior Photos');

  // ══════════════════════════════════════════════════════════════
  // Engine
  // ══════════════════════════════════════════════════════════════
  checkPage(60);
  sectionHeader('Engine Compartment');

  condTable2col(ENGINE_ITEMS, eng);

  if (eng.engineCondition) {
    checkPage(20);
    y += 4;
    kvRow('Overall condition', eng.engineCondition.charAt(0).toUpperCase() + eng.engineCondition.slice(1));
  }

  notesBlock('Engine notes', eng.engineNotes);

  const engPhotos = [
    ...(eng.eng_wide || []),
    ...(eng.eng_close || []),
    ...(eng.eng_issue_photos || []),
  ];
  await photoGrid(engPhotos, 'Engine Photos');

  // ══════════════════════════════════════════════════════════════
  // FOOTER on every page
  // ══════════════════════════════════════════════════════════════
  const pageCount = doc.getNumberOfPages();
  for (let p = 1; p <= pageCount; p++) {
    doc.setPage(p);
    doc.setDrawColor(...BORDER);
    doc.setLineWidth(0.5);
    doc.line(MARGIN, 772, W - MARGIN, 772);
    setFont('normal', 8);
    setColor(GRAY);
    doc.text(`Vehicle Condition Report  ·  ${vehicleTitle}  ·  ${dateStr}`, MARGIN, 784);
    doc.text(`Page ${p} of ${pageCount}`, W - MARGIN, 784, { align: 'right' });
  }

  return doc;
}

// ── Component ─────────────────────────────────────────────────────────────────

const ReportGenerator = ({ inspectionData }) => {
  const [generating, setGenerating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState(null);

  const vi = inspectionData.vehicleInfo || {};
  const fn = inspectionData.function_data || {};
  const ext = inspectionData.exterior_data || {};

  // Count issues
  const failedFunctions = FUNCTION_ITEMS.filter(i => fn[i.field] === 'fail').length;
  const damagedPanels = PANELS.filter(p => {
    const key = 'panel_' + p.toLowerCase().replace(/[^a-z]/g, '_');
    return ext[key] === 'damage';
  }).length;
  const totalIssues = failedFunctions + damagedPanels;

  const STEPS = [
    { key: 'vehicleInfo',          label: 'Vehicle Information' },
    { key: 'bol_data',             label: 'BOL Documentation' },
    { key: 'keys_data',            label: 'Keys & FOBs' },
    { key: 'function_data',        label: 'Vehicle Function' },
    { key: 'documentation_data',   label: 'Documentation' },
    { key: 'exterior_data',        label: 'Exterior Inspection' },
    { key: 'interior_data',        label: 'Interior Inspection' },
    { key: 'engine_data',          label: 'Engine Compartment' },
  ];

  const completedCount = STEPS.filter(s =>
    inspectionData[s.key] && Object.keys(inspectionData[s.key]).length > 0
  ).length;

  const handleGeneratePDF = async () => {
    setGenerating(true);
    setError(null);
    try {
      const doc = await buildPDF(inspectionData);
      const filename = [vi.year, vi.make, vi.model, vi.vin?.slice(-6)]
        .filter(Boolean).join('_').replace(/\s+/g, '_') + '_inspection.pdf';
      doc.save(filename);
      logAuditEvent({
        inspectionId: inspectionData.inspectionId,
        eventType: 'report_generated',
        metadata: { filename, pages: doc.getNumberOfPages() },
      });
    } catch (err) {
      console.error('PDF generation failed:', err);
      setError('PDF generation failed. Please try again.');
    } finally {
      setGenerating(false);
    }
  };

  const handleSave = async () => {
    if (!inspectionData.inspectionId) return;
    setSaving(true);
    setError(null);
    try {
      await updateInspection(inspectionData.inspectionId, {
        status: 'completed',
        total_issues: totalIssues,
        critical_issues: failedFunctions,
        inspector_notes: inspectionData.function_data?.functionNotes || null,
        damage_summary: inspectionData.exterior_data?.exteriorNotes || null,
      });
      logAuditEvent({
        inspectionId: inspectionData.inspectionId,
        eventType: 'status_changed',
        field: 'status',
        previousValue: { status: 'in_progress' },
        newValue: { status: 'completed' },
        metadata: { total_issues: totalIssues, critical_issues: failedFunctions },
      });
      setSaved(true);
    } catch (err) {
      console.error('Save failed:', err);
      setError('Failed to save inspection. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="step-content fade-in">
      <div className="step-header">
        <FileText className="w-8 h-8 text-blue-600" />
        <h2>Final Review</h2>
        <p>Review your inspection summary and generate the report</p>
      </div>

      {/* Vehicle card */}
      <div style={{
        background: 'linear-gradient(135deg, #1e293b, #334155)',
        borderRadius: 'var(--radius)', padding: '1.25rem',
        color: 'white',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.75rem' }}>
          <Car style={{ width: 20, height: 20, opacity: 0.7 }} />
          <span style={{ fontSize: '0.8rem', opacity: 0.7, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Vehicle</span>
        </div>
        <div style={{ fontSize: '1.3rem', fontWeight: 700, marginBottom: '0.25rem' }}>
          {[vi.year, vi.make, vi.model].filter(Boolean).join(' ') || 'Unknown Vehicle'}
        </div>
        <div style={{ fontSize: '0.85rem', opacity: 0.6, fontFamily: 'monospace' }}>
          {vi.vin || 'VIN not recorded'}
        </div>
        <div style={{ display: 'flex', gap: '1.5rem', marginTop: '1rem', fontSize: '0.85rem', opacity: 0.8 }}>
          {vi.odometer && <span>{Number(vi.odometer).toLocaleString()} mi</span>}
          {vi.location && <span>{vi.location}</span>}
          {vi.assetId && <span>Asset {vi.assetId}</span>}
        </div>
      </div>

      {/* Issue summary pills */}
      <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
        <div style={{
          flex: 1, minWidth: 100, padding: '1rem', textAlign: 'center',
          background: completedCount === 8 ? '#f0fdf4' : '#fefce8',
          border: `1px solid ${completedCount === 8 ? '#bbf7d0' : '#fde68a'}`,
          borderRadius: 'var(--radius-sm)',
        }}>
          <div style={{ fontSize: '1.5rem', fontWeight: 700, color: completedCount === 8 ? '#166534' : '#d97706' }}>
            {completedCount}/8
          </div>
          <div style={{ fontSize: '0.75rem', color: '#64748b', marginTop: 2 }}>Steps complete</div>
        </div>
        <div style={{
          flex: 1, minWidth: 100, padding: '1rem', textAlign: 'center',
          background: failedFunctions > 0 ? '#fef2f2' : '#f0fdf4',
          border: `1px solid ${failedFunctions > 0 ? '#fecaca' : '#bbf7d0'}`,
          borderRadius: 'var(--radius-sm)',
        }}>
          <div style={{ fontSize: '1.5rem', fontWeight: 700, color: failedFunctions > 0 ? '#dc2626' : '#166534' }}>
            {failedFunctions}
          </div>
          <div style={{ fontSize: '0.75rem', color: '#64748b', marginTop: 2 }}>Function failures</div>
        </div>
        <div style={{
          flex: 1, minWidth: 100, padding: '1rem', textAlign: 'center',
          background: damagedPanels > 0 ? '#fef2f2' : '#f0fdf4',
          border: `1px solid ${damagedPanels > 0 ? '#fecaca' : '#bbf7d0'}`,
          borderRadius: 'var(--radius-sm)',
        }}>
          <div style={{ fontSize: '1.5rem', fontWeight: 700, color: damagedPanels > 0 ? '#dc2626' : '#166534' }}>
            {damagedPanels}
          </div>
          <div style={{ fontSize: '0.75rem', color: '#64748b', marginTop: 2 }}>Damaged panels</div>
        </div>
      </div>

      {/* Step checklist */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
        {STEPS.map(step => {
          const done = inspectionData[step.key] && Object.keys(inspectionData[step.key]).length > 0;
          return (
            <div key={step.key} style={{
              display: 'flex', alignItems: 'center', gap: '0.75rem',
              padding: '0.75rem 1rem',
              background: done ? '#f0fdf4' : 'var(--surface-alt)',
              border: `1px solid ${done ? '#bbf7d0' : 'var(--border)'}`,
              borderRadius: 'var(--radius-sm)',
            }}>
              {done
                ? <CheckCircle2 style={{ width: 18, height: 18, color: '#166534', flexShrink: 0 }} />
                : <Clock style={{ width: 18, height: 18, color: '#d97706', flexShrink: 0 }} />
              }
              <span style={{ fontSize: '0.9rem', fontWeight: 500, color: done ? '#166534' : 'var(--text)' }}>
                {step.label}
              </span>
              <span style={{
                marginLeft: 'auto', fontSize: '0.75rem', fontWeight: 600,
                color: done ? '#166534' : '#d97706',
              }}>
                {done ? 'Complete' : 'Pending'}
              </span>
            </div>
          );
        })}
      </div>

      {error && (
        <div style={{
          padding: '0.875rem', background: '#fef2f2', border: '1px solid #fecaca',
          borderRadius: 'var(--radius-sm)', color: '#dc2626', fontSize: '0.9rem',
          display: 'flex', alignItems: 'center', gap: '0.5rem',
        }}>
          <AlertTriangle style={{ width: 16, height: 16, flexShrink: 0 }} />
          {error}
        </div>
      )}

      {/* Actions */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
        <button
          onClick={handleSave}
          disabled={saving || saved || !inspectionData.inspectionId}
          className="btn btn-secondary full-width"
          style={{ justifyContent: 'center' }}
        >
          {saved ? (
            <><CheckCircle2 className="w-5 h-5" style={{ color: '#166534' }} /> Saved to database</>
          ) : saving ? (
            'Saving...'
          ) : (
            <><FileText className="w-5 h-5" /> Mark inspection complete</>
          )}
        </button>

        <button
          onClick={handleGeneratePDF}
          disabled={generating}
          className="btn btn-primary full-width"
          style={{ justifyContent: 'center' }}
        >
          {generating
            ? 'Building PDF...'
            : <><Download className="w-5 h-5" /> Download PDF Report</>
          }
        </button>
      </div>
    </div>
  );
};

export default ReportGenerator;
